import React, { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import UserDashboardNav from "./UserDashboardNav";
import UserDashboardHeader from "./UserDashboardHeader";
import AuthUser from "../../Hooks/authUser";
import moment from "moment";
import { toast } from "react-toastify";
import Loading from "../../Shared/Loading";

/* ─── Page metadata map ──────────────────────────────── */
const PAGE_META = {
  overview: {
    title: "Dashboard Overview",
    subtitle: "Your visual therapy progress at a glance",
    icon: "solar:widget-5-bold",
  },
  reports: {
    title: "Clinical Reports",
    subtitle: "Detailed analytics & progress charts",
    icon: "solar:chart-2-bold",
  },
  settings: {
    title: "Account Settings",
    subtitle: "Lens calibration & preferences",
    icon: "solar:tuning-2-bold",
  },
  faq: {
    title: "Help & FAQ",
    subtitle: "Support center & frequently asked questions",
    icon: "solar:chat-round-dots-bold",
  },
  editProfile: {
    title: "Edit Profile",
    subtitle: "Update your personal information",
    icon: "solar:user-bold",
  },
  changePassword: {
    title: "Security",
    subtitle: "Update your login credentials",
    icon: "solar:lock-keyhole-minimalistic-bold",
  },
};

const getPageMeta = (pathname) => {
  const segment = Object.keys(PAGE_META).find((k) => pathname.includes(k));
  return PAGE_META[segment] ?? { title: "Patient Portal", subtitle: "Visual therapy workspace", icon: "solar:home-2-bold" };
};

/* ─── Component ──────────────────────────────────────── */
const UserDashboard = () => {
  const { userInfo } = AuthUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tick, setTick] = useState(0); // force clock re-render
  const [loading, setLoading] = useState(false);

  const pageMeta = getPageMeta(location.pathname);

  /* live clock — tick every 30s */
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  /* close mobile drawer on route change */
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  /* package guard */
  useEffect(() => {
    if (!userInfo?._id) return;
    setLoading(true);
    const issueDate = moment().format("YYYY-MM-DD");

    fetch(
      `https://servercareseebd.careseebd.com/api/v1/package/specific?fieldName=userId&&fieldValue=${userInfo._id}`
    )
      .then((r) => r.json())
      .then((data) => {
        if (data?.data?.length) {
          const expired = data.data[0]?.expireDate
            ? moment(issueDate).isAfter(data.data[0].expireDate)
            : true;
          if (expired) {
            toast.error("You don't have an active package. Contact your doctor.");
            navigate("/");
          }
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userInfo?._id]);

  const now = new Date();
  const timeStr = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  const dateStr = now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const greeting = (() => {
    const h = now.getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  if (loading) return <Loading />;

  return (
    <div
      className="flex h-screen overflow-hidden font-sans"
      style={{ background: "linear-gradient(135deg, #0d1117 0%, #0f1a2e 100%)" }}
    >
      {/* ── Desktop Sidebar ── */}
      <div className="hidden lg:flex flex-shrink-0 py-4 pl-4">
        <div className="rounded-2xl overflow-hidden h-full" style={{ boxShadow: "0 25px 50px rgba(0,0,0,0.5)" }}>
          <UserDashboardNav />
        </div>
      </div>

      {/* ── Mobile Drawer ── */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          {/* Drawer */}
          <div className="relative z-10 flex h-full">
            <UserDashboardNav onClose={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* ── Main panel ── */}
      <div className="flex-1 flex flex-col min-w-0 py-4 pr-4 pl-4 lg:pl-4 gap-0">
        <div
          className="flex-1 flex flex-col overflow-hidden rounded-2xl"
          style={{
            background: "#f8fafc",
            boxShadow: "0 25px 50px rgba(0,0,0,0.4)",
          }}
        >

          {/* ── Topbar ── */}
          <header
            className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-100"
            style={{ background: "rgba(255,255,255,0.95)", backdropFilter: "blur(12px)" }}
          >
            {/* Left — hamburger + page title */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all"
              >
                <Icon icon="solar:hamburger-menu-linear" className="text-xl" />
              </button>

              {/* Page icon + title */}
              <div className="flex items-center gap-3">
                <div
                  className="hidden sm:flex w-9 h-9 items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: "linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)" }}
                >
                  <Icon icon={pageMeta.icon} className="text-white text-[17px]" />
                </div>
                <div>
                  <h1 className="text-[15px] font-black text-slate-800 leading-tight tracking-tight">
                    {pageMeta.title}
                  </h1>
                  <p className="hidden sm:block text-[11px] font-semibold text-slate-400 leading-none mt-0.5">
                    {pageMeta.subtitle}
                  </p>
                </div>
              </div>
            </div>

            {/* Right — greeting + clock + avatar dropdown */}
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Greeting + date */}
              <div className="hidden md:flex flex-col items-end">
                <span className="text-[12px] font-bold text-slate-700">
                  {greeting}, {userInfo?.name?.split(" ")[0] ?? "there"} 👋
                </span>
                <span className="text-[10px] font-semibold text-slate-400 mt-0.5">
                  {dateStr} · {timeStr}
                </span>
              </div>

              {/* Divider */}
              <div className="hidden md:block w-px h-8 bg-slate-200" />

              {/* Status pill */}
              <div
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-emerald-700"
                style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.15)" }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"
                  style={{ boxShadow: "0 0 6px rgba(52,211,153,0.6)" }}
                />
                Active
              </div>

              {/* Avatar + dropdown */}
              <div className="flex items-center gap-2">
                <div
                  className="w-9 h-9 rounded-xl overflow-hidden flex-shrink-0"
                  style={{ boxShadow: "0 0 0 2px #e2e8f0" }}
                >
                  <UserDashboardHeader />
                </div>
              </div>
            </div>
          </header>

          {/* ── Subtle gradient separator ── */}
          <div
            className="h-px flex-shrink-0"
            style={{ background: "linear-gradient(90deg, transparent, rgba(99,102,241,0.15), transparent)" }}
          />

          {/* ── Content body ── */}
          <main
            className="flex-1 overflow-y-auto"
            style={{ background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)" }}
          >
            <div className="max-w-7xl mx-auto px-5 py-6 sm:px-8 sm:py-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
